import { inject } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  ResolveFn,
  RouterStateSnapshot,
} from "@angular/router";
import { Store } from "@ngrx/store";
import { AttributionCodeExtractorService } from "src/app/shared/services/attribution-code-extractor.service";
import { DataAnalyticsExtractorService } from "src/app/shared/services/data-analytics/data-analytics-extractor.service";
import { UrlService } from "src/app/shared/services/url.service";
import { AppState } from "src/app/shared/store/app.state";
import { storeQueryStringParameters } from "src/app/shared/store/quote/term-life-insurance/quote.actions";

/**
 * The query string parameters are being deleted after the page has been submitted.
 * See {@link QuoteEffects}
 */
export const extractQueryStringParametersResolver: ResolveFn<null> = (
  _: ActivatedRouteSnapshot,
  routerStateSnapshot: RouterStateSnapshot,
) => {
  const store = inject(Store<AppState>);
  const dataAnalyticsExtractorService = inject(DataAnalyticsExtractorService);
  const attributionCodeExtractorService = inject(
    AttributionCodeExtractorService,
  );
  const urlService = inject(UrlService);

  const currentUrl = urlService.createAbsoluteUrlByRelativePath(
    routerStateSnapshot.url,
  );

  const dataAnalyticsCampaign = tryGetDataAnalyticsCampaign(
    dataAnalyticsExtractorService,
    currentUrl,
  );

  const attributionCode =
    attributionCodeExtractorService.tryGetAttributionCode(currentUrl);

  const hasFoundAnyQueryStringParameters =
    dataAnalyticsCampaign || attributionCode;

  if (hasFoundAnyQueryStringParameters) {
    store.dispatch(
      storeQueryStringParameters({
        dataAnalyticsCampaign: dataAnalyticsCampaign,
        attributionCode: attributionCode,
      }),
    );
  }

  return null;
};

function tryGetDataAnalyticsCampaign(
  dataAnalyticsExtractor: DataAnalyticsExtractorService,
  url: URL,
) {
  const hasAtLeastOneUtmParameterInUrl =
    dataAnalyticsExtractor.hasAtLeastOneUtmParameterInUrl(url);

  const hasAtLeastOneUtmParameterInCookieUrl =
    dataAnalyticsExtractor.hasAtLeastOneUtmParameterInCookieUrl();

  /**
   * We do not expect a 'quoteId' in combination with utm parameters.
   */
  if (hasAtLeastOneUtmParameterInUrl) {
    dataAnalyticsExtractor.throwExceptionIfQuoteIdExists();
  }

  /**
   * The url has a higher precedence than a url from the cookie.
   * If we find utm parameters in the url, we ignore the values from the cookie url.
   */
  if (hasAtLeastOneUtmParameterInUrl) {
    return dataAnalyticsExtractor.getDataAnalyticsCampaignFromUrl();
  }

  if (hasAtLeastOneUtmParameterInCookieUrl) {
    return dataAnalyticsExtractor.tryGetDataAnalyticsCampaignFromCookieUrl();
  }

  return undefined;
}
